import { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { RealtimeChannel } from '@supabase/supabase-js';

interface UseSupabaseListenerOptions<T> {
  /** Supabase table name to watch */
  table: string;
  /** Realtime filter string e.g. "user_record_id=eq.abc123" */
  filter?: string;
  /** Postgres change events to listen for (default: '*') */
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  /** Fetches the current full dataset; called on mount and after each change event */
  fetchInitial: () => Promise<T[]>;
  /** When true the hook does nothing (debug mode) */
  disabled?: boolean;
}

interface UseSupabaseListenerResult<T> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Replaces useFirestoreListener. Sets up a Supabase Realtime subscription
 * and an initial data fetch. Re-fetches on every change event.
 *
 * Usage:
 *   const { data, isLoading } = useSupabaseListener({
 *     table: 'notifications',
 *     filter: `user_record_id=eq.${userRecord.id}`,
 *     fetchInitial: async () => {
 *       const { data } = await supabase.from('notifications').select().eq('user_record_id', userRecord.id);
 *       return data ?? [];
 *     },
 *   }, [userRecord?.id]);
 */
export function useSupabaseListener<T>(
  options: UseSupabaseListenerOptions<T>,
  deps: any[] = [],
): UseSupabaseListenerResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const cancelledRef = useRef(false);

  // Stable reference to the fetchInitial function
  const fetchInitialRef = useRef(options.fetchInitial);
  fetchInitialRef.current = options.fetchInitial;

  useEffect(() => {
    if (options.disabled) {
      setIsLoading(false);
      return;
    }

    cancelledRef.current = false;
    setIsLoading(true);

    // 1. Fetch initial data
    fetchInitialRef.current()
      .then((rows) => {
        if (!cancelledRef.current) {
          setData(rows);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelledRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    // 2. Subscribe to Realtime changes
    const channelName = [
      options.table,
      options.filter ?? 'all',
      Date.now(),
    ].join('-');

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        {
          event: options.event ?? '*',
          schema: 'public',
          table: options.table,
          filter: options.filter,
        },
        () => {
          // Re-fetch full dataset on any change for simplicity
          if (!cancelledRef.current) {
            fetchInitialRef.current()
              .then((rows) => {
                if (!cancelledRef.current) setData(rows);
              })
              .catch((err) => {
                if (!cancelledRef.current)
                  setError(err instanceof Error ? err : new Error(String(err)));
              });
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      cancelledRef.current = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error };
}
